// ABOUTME: Timeline component with consistent styling
// ABOUTME: Provides compound components for timeline layouts

import * as React from "react"
import { cn } from "@shared/lib/utils"

export interface CTimelineProps extends React.HTMLAttributes<HTMLOListElement> {
  horizontal?: boolean
}

export type CTimelineItemProps = React.HTMLAttributes<HTMLLIElement>

export type CTimelinePointProps = React.HTMLAttributes<HTMLDivElement> & {
  icon?: React.ComponentType<{ className?: string }>
}

export type CTimelineContentProps = React.HTMLAttributes<HTMLDivElement>

export type CTimelineTitleProps = React.HTMLAttributes<HTMLHeadingElement>

export type CTimelineBodyProps = React.HTMLAttributes<HTMLParagraphElement>

export type CTimelineTimeProps = React.TimeHTMLAttributes<HTMLTimeElement>

type CTimelineComponent = React.FC<CTimelineProps> & {
  Item: React.FC<CTimelineItemProps>
  Point: React.FC<CTimelinePointProps>
  Content: React.FC<CTimelineContentProps>
  Title: React.FC<CTimelineTitleProps>
  Body: React.FC<CTimelineBodyProps>
  Time: React.FC<CTimelineTimeProps>
}

const CTimelineBase: React.FC<CTimelineProps> = ({ className, horizontal, children, ...props }) => {
  return (
    <ol
      className={cn("relative", horizontal ? "flex items-center" : "border-l border-gray-200", className)}
      {...props}
    >
      {children}
    </ol>
  )
}

const CTimelineItem: React.FC<CTimelineItemProps> = ({ className, children, ...props }) => {
  return (
    <li
      className={cn("mb-10 ml-6", className)}
      {...props}
    >
      {children}
    </li>
  )
}

const CTimelinePoint: React.FC<CTimelinePointProps> = ({ className, icon: Icon, ...props }) => {
  return (
    <div
      className={cn(
        "absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 ring-8 ring-white",
        className,
      )}
      {...props}
    >
      {Icon && <Icon className="h-3 w-3 text-blue-600" />}
    </div>
  )
}

const CTimelineContent: React.FC<CTimelineContentProps> = ({ className, children, ...props }) => {
  return (
    <div
      className={cn("", className)}
      {...props}
    >
      {children}
    </div>
  )
}

const CTimelineTitle: React.FC<CTimelineTitleProps> = ({ className, children, ...props }) => {
  return (
    <h3
      className={cn("text-lg font-semibold text-gray-900", className)}
      {...props}
    >
      {children}
    </h3>
  )
}

const CTimelineBody: React.FC<CTimelineBodyProps> = ({ className, children, ...props }) => {
  return (
    <p
      className={cn("text-base font-normal text-gray-500", className)}
      {...props}
    >
      {children}
    </p>
  )
}

const CTimelineTime: React.FC<CTimelineTimeProps> = ({ className, children, ...props }) => {
  return (
    <time
      className={cn("mb-1 text-sm font-normal leading-none text-gray-400", className)}
      {...props}
    >
      {children}
    </time>
  )
}

const CTimelineWithStatics: CTimelineComponent = Object.assign(CTimelineBase, {
  Item: CTimelineItem,
  Point: CTimelinePoint,
  Content: CTimelineContent,
  Title: CTimelineTitle,
  Body: CTimelineBody,
  Time: CTimelineTime,
})

// Exported under its own identifier: Fast Refresh (react-refresh/only-export-components)
// recognises a component by the name, and the Object.assign that attaches the statics is opaque to it.
export const CTimeline = CTimelineWithStatics
